import {
  createTheme,
  Datepicker,
  Dropdown,
  DropdownItem,
  TextInput,
} from 'flowbite-react';
import { Icon } from '@acme/assets';
import { useState, MouseEvent } from 'react';
import { formatDate } from '../../utils';
import { customTheme } from '../../theme';
import { twMerge } from 'tailwind-merge';

const currentMonth = new Date(
  new Date().getFullYear(),
  new Date().getMonth(),
  1,
  0,
  0,
  0
);
const nextMonth = new Date(currentMonth);
nextMonth.setMonth(currentMonth.getMonth() + 1);

const buttonTheme = customTheme.button.color.primary;

type DatePeriod = {
  start: Date | null;
  end: Date | null;
};

type DatePickerRangeProps = {
  value?: { start: string | null; end: string | null };
  onChange: (value: { start: string | null; end: string | null }) => void;
};

const defaultPeriod = { start: null, end: null };

const datePickerTheme = (type: 'start' | 'end', date: Date | null) => {
  return createTheme({
    popup: {
      root: {
        inner: 'bg-transparent shadow-none p-0',
      },
      header: {
        selectors: {
          button: {
            view: `capitalize w-[195px] before:text-gray-500 before:font-semibold before:mr-1 ${type === 'start' ? "before:content-['От:']" : "before:content-['До:']"}`,
          },
        },
      },
      view: {
        base: 'p-1',
      },
    },
    views: {
      days: {
        header: {
          title: 'uppercase',
        },
        items: {
          base: 'w-full',
          item: {
            base: `block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600`,
            selected: `text-white bg-brand-purple-900 hover:bg-brand-purple-900 rounded-none ${type === 'start' ? 'rounded-l-lg' : 'rounded-r-lg'} ${!date && 'bg-transparent text-gray-900 hover:bg-gray-100 rounded-lg'}`,
            disabled: 'text-gray-300 hover:bg-transparent cursor-not-allowed',
          },
        },
      },
    },
  });
};

export const DatePickerRange = (props: DatePickerRangeProps) => {
  const [period, setPeriod] = useState<DatePeriod>({
    start: props.value?.start ? new Date(props.value?.start) : null,
    end: props.value?.end ? new Date(String(props.value?.end)) : null,
  });
  const [inputValue, setInputValue] = useState<string>('');

  const handleChangePeriod =
    (field: 'start' | 'end') => (value: Date | null) => {
      setPeriod((prev) => ({ ...prev, [field]: value }));
    };

  const handleClearPeriod = (e: MouseEvent) => {
    e.stopPropagation();

    setPeriod({ ...defaultPeriod });
    setInputValue('');
    props.onChange(defaultPeriod);
  };

  const handleSetPeriod = () => {
    if (!period.start || !period.end) {
      return;
    }

    setInputValue(
      `${formatDate(period.start).replace(/\./g, '/')} — ${formatDate(period.end).replace(/\./g, '/')}`
    );

    props.onChange({
      start: period.start.toISOString(),
      end: period.end.toISOString(),
    });
  };

  return (
    <div>
      <Dropdown
        theme={{
          content: 'grid grid-cols-2 gap-x-8 gap-y-4 p-4 cursor-default',
          floating: {
            item: {
              base: twMerge(
                buttonTheme,
                'focus:bg-brand-purple-500 relative flex items-center justify-center rounded-lg text-center font-medium focus:outline-none focus:ring-4 disabled:pointer-events-none disabled:opacity-50'
              ),
            },
          },
        }}
        renderTrigger={() => (
          <div className="relative group">
            <TextInput
              value={inputValue}
              theme={{
                field: {
                  rightIcon: {
                    base: 'pointer-events-auto absolute inset-y-0 right-0 flex items-center pr-3',
                  },
                },
              }}
              type="text"
              readOnly
              placeholder="Выберите период"
              icon={() => (
                <Icon name="calendar-month" className="text-gray-500" />
              )}
              rightIcon={() => {
                if (!inputValue) {
                  return null;
                }

                return (
                  <button
                    type="button"
                    className="flex items-center justify-center text-gray-500 hover:text-gray-700"
                    aria-label="Сбросить"
                    onClick={handleClearPeriod}
                  >
                    <Icon name="x-outline" size={12} />
                  </button>
                );
              }}
            />
          </div>
        )}
      >
        <Datepicker
          theme={datePickerTheme('start', period.start)}
          value={period.start}
          onChange={handleChangePeriod('start')}
          inline
          language="ru"
          showTodayButton={false}
          showClearButton={false}
          weekStart={1}
          maxDate={period.end ? new Date(period.end) : undefined}
        />
        <Datepicker
          theme={datePickerTheme('end', period.end)}
          value={period.end}
          defaultValue={
            new Date(new Date().getFullYear(), new Date().getMonth() + 1)
          }
          onChange={handleChangePeriod('end')}
          inline
          language="ru"
          showTodayButton={false}
          showClearButton={false}
          weekStart={1}
          minDate={period.start ? new Date(period.start) : undefined}
        />

        <DropdownItem
          onClick={handleSetPeriod}
          disabled={!period.start || !period.end}
        >
          Применить
        </DropdownItem>
      </Dropdown>
    </div>
  );
};
